import React from 'react';

import TrainingPilot from '@rover/icons/svg/training-pilot.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock/IconBlock';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import type { BundleOffering } from '@rover/rsdk/src/apiClient/latest/model/bundleOffering';
import { TrainingConsultationServiceAddOnRetrieve } from '@rover/rsdk/src/apiClient/latest/model/trainingConsultationServiceAddOnRetrieve';
import { formatCurrency } from '@rover/utilities/currency';

import ServiceRow from './ServiceRow';

type DogTrainingServiceBoxProps = {
  rate: string;
  locale: string;
};

const DogTrainingServiceBox = ({ rate, locale }: DogTrainingServiceBoxProps): JSX.Element => {
  const textSize = '300';
  return (
    <Flex width="100%" justifyContent="space-between" data-testid="service-detail-header">
      <Box pr="3x">
        <IconBlock icon={TrainingPilot} title="Dog Training" size={textSize}>
          <Text size="100" textColor="tertiary">
            {/* TODO (DEV-104195): Translate below text after training pilot launch */}
            in your home
          </Text>
        </IconBlock>
      </Box>
      <Flex mt="1x">
        <Flex flexDirection="column" alignItems="end" textAlign="right">
          <Text fontWeight="semibold" size={textSize}>
            {formatCurrency(rate, 'USD', locale, { includeDecimal: false })}
          </Text>
          <Text size="100" textColor="secondary">
            {/* TODO (DEV-104195): Translate below text after training pilot launch */}
            per session
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

type TrainingServiceHeaderProps = {
  rate: string;
  consultations: TrainingConsultationServiceAddOnRetrieve[];
  bundleOfferings: BundleOffering[];
  bundleChangesActive: boolean;
  showVirtualBadging: boolean;
  shouldUsePriceTransparency?: boolean;
};

const TrainingServiceHeader = ({
  rate,
  consultations,
  bundleOfferings,
  bundleChangesActive,
  showVirtualBadging,
  shouldUsePriceTransparency = false,
}: TrainingServiceHeaderProps): JSX.Element => {
  const { locale } = useI18nContext();

  const virtualConsultations = consultations.find(
    (consultation) => consultation.type === 'virtual'
  );

  const inPersonConsultations = consultations.find(
    (consultation) => consultation.type === 'in-person'
  );

  const getPriceText = (price: string): string => {
    return Number(price) > 0
      ? formatCurrency(price, 'USD', locale, {
          includeDecimal: false,
        })
      : 'Free';
  };

  const getBundlePricePerSessionText = (bundle: BundleOffering): string => {
    const { sessionCount, pricePerSession, fullPricePerSession } = bundle;
    const price =
      shouldUsePriceTransparency && fullPricePerSession !== null
        ? fullPricePerSession
        : pricePerSession;

    if (sessionCount === 1) {
      return `${getPriceText(price)} x 1 session`;
    }
    return `${getPriceText(price)} x ${sessionCount} sessions`;
  };

  const getBundleTotalPriceText = (bundle: BundleOffering): string => {
    const price =
      shouldUsePriceTransparency && bundle.fullTotalPrice !== null
        ? bundle.fullTotalPrice
        : bundle.totalPrice;
    return getPriceText(price);
  };

  const getConsultationPriceText = (
    consultation: TrainingConsultationServiceAddOnRetrieve
  ): string => {
    const price =
      shouldUsePriceTransparency && consultation.fullPrice !== null
        ? consultation.fullPrice
        : consultation.price;

    return getPriceText(price);
  };

  const getConsultationHeader = (): string => {
    if (showVirtualBadging) {
      return 'Virtual consultation';
    }
    return 'Consultation';
  };

  const consultationsSection = (
    <>
      {bundleChangesActive && (
        <Heading size="300" mb="2x">
          {/* TODO (DEV-104195): Translate */}
          Consultations
        </Heading>
      )}
      <Flex flexDirection="column" width="100%" gap="6x" mb="4x">
        {inPersonConsultations && (
          <ServiceRow
            header={getConsultationHeader()}
            priceText={getConsultationPriceText(inPersonConsultations)}
          />
        )}
        {virtualConsultations && (
          <ServiceRow
            header="Virtual consultation"
            priceText={getConsultationPriceText(virtualConsultations)}
          />
        )}
      </Flex>
    </>
  );

  const sessionsSection = (
    <>
      <Heading size="300" mb="2x">
        {/* TODO (DEV-104195): Translate */}
        Sessions
      </Heading>
      <Flex flexDirection="column" width="100%" gap="6x">
        <ServiceRow
          header="Single session"
          priceText={getPriceText(rate)}
          description="per session"
        />
        {bundleOfferings.map((bundle) => (
          <ServiceRow
            key={bundle.opk}
            header={bundle.name}
            priceText={getBundleTotalPriceText(bundle)}
            description={getBundlePricePerSessionText(bundle)}
          />
        ))}
      </Flex>
    </>
  );

  return (
    <Flex flexDirection="column" width="100%" gap="6x">
      {!bundleChangesActive && <DogTrainingServiceBox rate={rate} locale={locale} />}
      {(inPersonConsultations || virtualConsultations) && consultationsSection}
      {bundleChangesActive && sessionsSection}
    </Flex>
  );
};

export default TrainingServiceHeader;
